import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  IconButton,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  Campaign as CampaignIcon,
  Article as ArticleIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { notificationsAPI, type Notification, type NotificationType } from '../services/api';

interface NotificationBellProps {
  userId: number;
}

// Иконки для разных типов уведомлений
const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'event_created':
    case 'event_updated':
      return <EventIcon color="info" />;
    case 'event_cancelled':
      return <CancelIcon color="error" />;
    case 'application_approved':
      return <CheckCircleIcon color="success" />;
    case 'application_rejected':
    case 'application_received':
      return <AccessTimeIcon color="warning" />;
    case 'new_participant':
    case 'participant_left':
      return <PeopleIcon color="info" />;
    case 'free_slots':
      return <NotificationsIcon color="success" />;
    case 'reminder':
      return <CampaignIcon color="warning" />;
    case 'blog_article':
      return <ArticleIcon color="info" />;
    case 'blog_comment':
      return <ChatIcon color="success" />;
    default:
      return <NotificationsIcon />;
  }
};

// Цвет аватара в зависимости от типа
const getAvatarColor = (type: NotificationType) => {
  switch (type) {
    case 'event_created':
    case 'event_updated':
      return '#2196f3';
    case 'event_cancelled':
      return '#f44336';
    case 'application_approved':
      return '#4caf50';
    case 'application_rejected':
    case 'application_received':
      return '#ff9800';
    case 'new_participant':
    case 'participant_left':
      return '#9c27b0';
    case 'free_slots':
      return '#00bcd4';
    case 'reminder':
      return '#ff5722';
    case 'blog_article':
      return '#3f51b5';
    case 'blog_comment':
      return '#009688';
    default:
      return '#757575';
  }
};

// Форматирование даты
const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'только что';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} мин. назад`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ч. назад`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} дн. назад`;
  
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'numeric',
  });
};

const NotificationBell: React.FC<NotificationBellProps> = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [blogUnreadCount, setBlogUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'events' | 'blog'>('events');

  // Загрузка уведомлений
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsAPI.getNotifications(50, 0, false);
      setNotifications(data.notifications);
      // Count unread blog notifications (articles + comments) for blue badge
      const blogTypes = ['blog_article', 'blog_comment'];
      const blogCount = data.notifications.filter((n: Notification) => !n.is_read && blogTypes.includes(n.type)).length;
      setBlogUnreadCount(blogCount);
      // Red badge = all unread MINUS blog notifications
      const eventCount = data.notifications.filter((n: Notification) => !n.is_read && !blogTypes.includes(n.type)).length;
      setUnreadCount(eventCount);
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
    } finally {
      setLoading(false);
    }
  };

  // Периодическая проверка новых уведомлений (каждые 30 секунд)
  useEffect(() => {
    loadNotifications();
    
    const interval = setInterval(() => {
      loadNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    if (!open) {
      loadNotifications();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleMarkAsRead = async () => {
    try {
      await notificationsAPI.markAsRead({ mark_all: true });
      setUnreadCount(0);
      setBlogUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Ошибка отметки уведомлений:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Если уведомление не прочитано, помечаем как прочитанное
    if (!notification.is_read) {
      notificationsAPI.markAsRead({ notification_ids: [notification.id] });
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      );
      const blogTypes = ['blog_article', 'blog_comment'];
      if (blogTypes.includes(notification.type)) {
        setBlogUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    }

    // Переход по ссылке из уведомления
    if (notification.link) {
      navigate(notification.link);
    }
    // Переход к мероприятию
    else if (notification.event_id) {
      navigate(`/events/${notification.event_id}`);
    }
    
    handleClose();
  };

  const handleViewAll = () => {
    navigate('/notifications');
    handleClose();
  };

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? 'notification-popover' : undefined;

  return (
    <>
      <Tooltip title="Уведомления">
        <IconButton
          onClick={handleClick}
          size="large"
          sx={{ color: 'inherit', position: 'relative' }}
        >
          {/* Blue blog badge — top left */}
          {blogUnreadCount > 0 && (
            <Box sx={{
              position: 'absolute', top: 2, left: 2,
              width: 18, height: 18, borderRadius: '50%',
              bgcolor: '#1565c0', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.6rem', fontWeight: 700, zIndex: 1,
            }}>
              {blogUnreadCount > 9 ? '9+' : blogUnreadCount}
            </Box>
          )}
          <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error">
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 500,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            Уведомления
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                color="primary"
                onClick={handleMarkAsRead}
                startIcon={<CheckCircleIcon />}
                sx={{ fontSize: '0.75rem' }}
              >
                Прочитать
              </Button>
            )}
            <Button
              size="small"
              onClick={handleViewAll}
              sx={{ fontSize: '0.75rem' }}
            >
              Все
            </Button>
          </Box>
        </Box>

        {/* Tab toggle */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
          <Box
            onClick={() => setActiveTab('events')}
            sx={{
              flex: 1, py: 0.75, textAlign: 'center', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600,
              color: activeTab === 'events' ? '#1565c0' : '#64748b',
              borderBottom: activeTab === 'events' ? '2px solid #1565c0' : '2px solid transparent',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: '#f5f7fa' },
            }}
          >
            Мероприятия {unreadCount - blogUnreadCount > 0 && `(${unreadCount - blogUnreadCount})`}
          </Box>
          <Box
            onClick={() => setActiveTab('blog')}
            sx={{
              flex: 1, py: 0.75, textAlign: 'center', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600,
              color: activeTab === 'blog' ? '#1565c0' : '#64748b',
              borderBottom: activeTab === 'blog' ? '2px solid #1565c0' : '2px solid transparent',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: '#f5f7fa' },
            }}
          >
            Блог {blogUnreadCount > 0 && `(${blogUnreadCount})`}
          </Box>
        </Box>

        <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
          {(() => {
            const filtered = notifications.filter(n =>
              activeTab === 'blog' ? n.type === 'blog_article' || n.type === 'blog_comment'
                : n.type !== 'blog_article' && n.type !== 'blog_comment'
            );
            return loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {activeTab === 'blog' ? 'Нет уведомлений из блога' : 'Нет уведомлений по мероприятиям'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filtered.map((notification) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: getAvatarColor(notification.type),
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={notification.is_read ? 'normal' : 'bold'}
                          sx={{
                            color: notification.is_read ? 'text.secondary' : 'text.primary',
                          }}
                        >
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              color: notification.is_read ? 'text.secondary' : 'text.primary',
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatTimeAgo(notification.created_at)}
                          </Typography>
                        </>
                      }
                    />
                    {!notification.is_read && (
                      <ListItemSecondaryAction>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        />
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
            );
          })()}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
